import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useQueryClient } from '@tanstack/react-query';
import { TEAMS_BOT } from '@elementary/common-ui/constants/queryKeys';
import {
  Alert,
  Button,
  FlexColumn,
  Link,
} from '@elementary/common-ui/components';
import { Typography } from '@mui/material';
import useSupportChat from 'shared/hooks/useSupportChat';
import { ConnectedWorkspace } from 'shared/types';
import IntegrationDialog from '../../shared/Integration/IntegrationDialog';
import TestIntegrationSection from './IntegrationTestSections/TestIntegrationSection';
import TeamsBotTestSection from './IntegrationTestSections/TeamsBotTestSection';
import TeamsBotDefaultChannelSection from './TeamsBotDefaultChannelSection';

const InstructionsList = styled.ol`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 24px;
  margin: 0;
`;

const InstructionItem = styled.li``;

const ConnectedDetails = styled(FlexColumn)`
  gap: 8px;
  padding: 16px;
  border-radius: 8px;
  background: ${({ theme }) => theme.palette.common.gray14};
`;

const ConnectingPanel = styled(FlexColumn)`
  align-items: center;
  gap: 16px;
  padding: 32px 16px;
  text-align: center;
`;

const GrayText = styled(Typography)`
  color: ${({ theme }) => theme.palette.common.gray2};
`;

const MonospaceText = styled(Typography)`
  font-family: monospace;
  font-size: 14px;
`;

const ConnectingTitle = styled(Typography)`
  font-weight: bold;
`;

const ConnectingDescription = styled(Typography)`
  color: ${({ theme }) => theme.palette.common.gray2};
  max-width: 480px;
`;

const ConnectedSection = styled(FlexColumn)`
  gap: 8px;
`;

const ConnectedFieldLabel = styled(Typography).attrs({ variant: 'body2' })<{
  $first?: boolean;
}>`
  color: ${({ theme }) => theme.palette.common.gray2};
  margin-top: ${({ $first }) => ($first ? 0 : '8px')};
`;

const DialogBody = styled(FlexColumn)`
  gap: 24px;
`;

export type TeamsBotDialogStep = 'setup' | 'connecting' | 'connected';

type Props = {
  open: boolean;
  onClose: () => void;
  isConnected: boolean;
  isLoading?: boolean;
  manifestUrl?: string;
  appId?: string;
  envId: string;
  connectedWorkspaces?: ConnectedWorkspace[];
  initialStep?: TeamsBotDialogStep;
  connectError?: string | null;
  isLinking?: boolean;
  onDisconnect?: () => void;
};

const TEAMS_APP_BASE_URL = 'https://teams.microsoft.com';

const TeamsBotDialog = ({
  open,
  onClose,
  isConnected,
  isLoading,
  manifestUrl,
  appId,
  envId,
  connectedWorkspaces = [],
  initialStep,
  connectError,
  isLinking,
  onDisconnect,
}: Props) => {
  const [testSuccess, setTestSuccess] = useState(false);
  const [step, setStep] = useState<TeamsBotDialogStep>(
    initialStep ?? (isConnected ? 'connected' : 'setup'),
  );
  const queryClient = useQueryClient();
  const supportChat = useSupportChat();

  useEffect(() => {
    if (open) {
      void queryClient.invalidateQueries([TEAMS_BOT]);
    }
  }, [open, queryClient]);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (isConnected) {
      setStep('connected');
      return;
    }
    if (initialStep) {
      setStep(initialStep);
    }
  }, [open, isConnected, initialStep]);

  // While waiting for the user to connect from Teams, poll the bot settings so
  // the dialog auto-advances to "connected" once the link completes — no need
  // to close and reopen.
  useEffect(() => {
    if (!open || step !== 'connecting') {
      return undefined;
    }
    const intervalId = setInterval(() => {
      void queryClient.invalidateQueries([TEAMS_BOT]);
    }, 4000);
    return () => clearInterval(intervalId);
  }, [open, step, queryClient]);

  const dialogTitle = useMemo(() => {
    if (step === 'connected') {
      return 'Microsoft Teams integration';
    }
    if (step === 'connecting') {
      return 'Connecting Microsoft Teams';
    }
    return 'Connect Microsoft Teams (app)';
  }, [step]);

  const startConnecting = useCallback(() => {
    setStep('connecting');
  }, []);

  const dialogFooter = useMemo(() => {
    if (step === 'setup') {
      return {
        hideSaveButton: false,
        saveButtonText: 'Continue',
        onSubmit: startConnecting,
        isValid: !isLoading,
        cancelText: 'Close',
      };
    }
    if (step === 'connecting') {
      return {
        hideSaveButton: false,
        saveButtonText: 'Close',
        onSubmit: onClose,
        isValid: true,
        cancelText: 'Back to setup',
        onCancel: () => setStep('setup'),
      };
    }
    return {
      hideSaveButton: true,
      cancelText: 'Close',
    };
  }, [step, startConnecting, isLoading, onClose]);

  const renderSetupStep = () => (
    <>
      <GrayText>
        Connect your Microsoft Teams workspace to Elementary to receive data
        observability alerts directly in your Teams channels.
      </GrayText>

      <InstructionsList>
        <InstructionItem>
          <Typography>
            Install the Elementary app in Teams.{' '}
            {appId ? (
              <a
                href={`${TEAMS_APP_BASE_URL}/l/app/${appId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in Microsoft Teams
              </a>
            ) : (
              'Find Elementary in the Teams App Store, or contact your admin to get the app package.'
            )}
            {manifestUrl ? (
              <>
                {' '}
                (or{' '}
                <a href={manifestUrl} download="teams-manifest.zip">
                  download the manifest
                </a>
                )
              </>
            ) : null}
          </Typography>
        </InstructionItem>
        <InstructionItem>
          <Typography>
            Add the app to a team where you want to receive alerts.
          </Typography>
        </InstructionItem>
        <InstructionItem>
          <Typography>
            In Teams, open the welcome message and click{' '}
            <strong>Connect to Elementary</strong>.
          </Typography>
        </InstructionItem>
      </InstructionsList>
    </>
  );

  const renderConnectingStep = () => (
    <>
      <ConnectingPanel>
        <ConnectingTitle>
          {isLinking
            ? 'Linking your Teams workspace…'
            : 'Complete connection in Teams'}
        </ConnectingTitle>
        <ConnectingDescription>
          {isLinking
            ? 'Confirming the connection from Teams. This should only take a moment.'
            : 'Open the app welcome message in your Teams channel and click Connect to Elementary. This page updates automatically once you are connected.'}
        </ConnectingDescription>
      </ConnectingPanel>
      {connectError && (
        <Alert severity="error">
          {connectError} If the problem persists, please{' '}
          <Link onClick={supportChat.show} href="#">
            contact us
          </Link>
          .
        </Alert>
      )}
      {!connectError && (
        <Alert severity="info">
          Waiting for the connection from Teams… If you completed these steps
          and it doesn&apos;t update, please{' '}
          <Link onClick={supportChat.show} href="#">
            contact us
          </Link>{' '}
          for assistance.
        </Alert>
      )}
    </>
  );

  const renderConnectedStep = () => (
    <>
      <Alert severity="success">
        Your Microsoft Teams workspace is connected to Elementary.
      </Alert>
      <ConnectedSection>
        <ConnectingTitle>Connected workspace</ConnectingTitle>
        {connectedWorkspaces.length > 0 ? (
          connectedWorkspaces.map((workspace) => (
            <ConnectedDetails key={`${workspace.tenantId}-${workspace.teamId}`}>
              <ConnectedFieldLabel $first>
                Microsoft tenant ID
              </ConnectedFieldLabel>
              <MonospaceText>{workspace.tenantId}</MonospaceText>
              <ConnectedFieldLabel>Team ID</ConnectedFieldLabel>
              <MonospaceText>{workspace.teamId}</MonospaceText>
            </ConnectedDetails>
          ))
        ) : (
          <GrayText>No linked workspace details are available.</GrayText>
        )}
      </ConnectedSection>
      <ConnectedSection>
        <ConnectingTitle>Default channel</ConnectingTitle>
        <TeamsBotDefaultChannelSection envId={envId} />
      </ConnectedSection>
      <TestIntegrationSection isSuccess={testSuccess}>
        <TeamsBotTestSection
          onSuccess={() => setTestSuccess(true)}
          onReset={() => setTestSuccess(false)}
        />
      </TestIntegrationSection>
      <ConnectedSection>
        <ConnectingTitle>Disconnecting</ConnectingTitle>
        <GrayText>
          Disconnect unlinks this workspace from Elementary and stops alerts.
          The app stays installed in Microsoft Teams — remove it there (Manage
          team → Apps → remove Elementary) to fully uninstall.
        </GrayText>
        {onDisconnect && (
          <Button
            variant="text"
            color="error"
            onClick={onDisconnect}
            sx={{ alignSelf: 'flex-start' }}
          >
            Disconnect
          </Button>
        )}
        <a href={TEAMS_APP_BASE_URL} target="_blank" rel="noopener noreferrer">
          Open Microsoft Teams
        </a>
      </ConnectedSection>
    </>
  );

  return (
    <IntegrationDialog
      open={open}
      onClose={onClose}
      title={dialogTitle}
      hideSaveButton={dialogFooter.hideSaveButton}
      saveButtonText={dialogFooter.saveButtonText}
      onSubmit={dialogFooter.onSubmit}
      isValid={dialogFooter.isValid ?? true}
      cancelText={dialogFooter.cancelText}
      onCancel={dialogFooter.onCancel}
      width={720}
      loading={(isLoading && step === 'setup') || !!isLinking}
    >
      <DialogBody>
        {step === 'connected' && renderConnectedStep()}
        {step === 'connecting' && renderConnectingStep()}
        {step === 'setup' && renderSetupStep()}
      </DialogBody>
    </IntegrationDialog>
  );
};

export default TeamsBotDialog;
